import { Link } from "react-router-dom";
import {
  formatAud,
  URBAN_DEVELOPMENT_PER_LOT_CENTS,
  CONSULTANCY_FROM_CENTS,
  GUIDED_REPORT_FROM_CENTS,
  DIY_TOOLS_MAX_CENTS,
  addGst,
} from "@shared/types";
import {
  STANDARD_EA_MINING,
  standardEaMiningEnquiryPath,
  standardEaMiningMoney,
} from "@shared/standardEaMining";

const urban = addGst(URBAN_DEVELOPMENT_PER_LOT_CENTS);
const tier3 = addGst(CONSULTANCY_FROM_CENTS);
const standardEa = standardEaMiningMoney();

export function PricingPage() {
  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div>
            <h2>Clear prices. No surprise scope creep.</h2>
            <p>
              Pay for a tool, a report, or a full project — not a vague “engagement” you didn’t need.
              AUD, GST transparent, Stripe for self-serve purchases.
            </p>
          </div>
        </div>

        <div className="alert alert-info" style={{ marginBottom: "1.5rem" }}>
          <strong>Most people overspend on environmental work.</strong> If a free checklist or a
          ~48-hour guided report solves it, we’ll say so. Consultancy starts at{" "}
          {formatAud(CONSULTANCY_FROM_CENTS)} ex GST for a reason.
        </div>

        <div className="grid-3" style={{ marginBottom: "2rem" }}>
          <article className="card tier-card tier-card-preferred">
            <span className="badge badge-free">Instant</span>
            <h3 style={{ marginTop: "0.7rem" }}>DIY tools</h3>
            <p className="price">
              Free – {formatAud(DIY_TOOLS_MAX_CENTS)} <small>incl. GST</small>
            </p>
            <p>
              <strong>Pain:</strong> you need a template or field sheet now, not a proposal next
              month.
            </p>
            <p>
              <strong>Fix:</strong> download free and paid PDFs (checklists, canvases, survey packs)
              or buy Plant species ID from 3 photos. Use them on the next site visit.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <Link className="btn btn-primary btn-sm" to="/resources">
                Browse the library
              </Link>
              <Link className="btn btn-outline btn-sm" to="/resources/plant-species-id">
                Plant species ID
              </Link>
            </div>
          </article>
          <article className="card tier-card tier-card-preferred">
            <span className="badge badge-paid">~48 hours</span>
            <h3 style={{ marginTop: "0.7rem" }}>Guided reports</h3>
            <p className="price">
              From {formatAud(GUIDED_REPORT_FROM_CENTS)} <small>ex GST</small>
            </p>
            <p>
              <strong>Pain:</strong> you need a professional report, but full consultancy is too
              slow and too expensive for a standard pathway.
            </p>
            <p>
              <strong>Fix:</strong> complete a questionnaire, upload files and logo; we review and
              release a co-branded PDF — target about 48 hours.
            </p>
            <Link className="btn btn-primary btn-sm" to="/reports">
              Start a guided report
            </Link>
          </article>
          <article className="card tier-card">
            <span className="badge badge-status">Scoped</span>
            <h3 style={{ marginTop: "0.7rem" }}>Consultancy</h3>
            <p className="price">
              From {formatAud(CONSULTANCY_FROM_CENTS)} <small>ex GST</small>
            </p>
            <p>
              <strong>Pain:</strong> multi-agency, high-risk or multi-site work that can’t be boxed
              into a template.
            </p>
            <p>
              <strong>Fix:</strong> scoped proposal after enquiry. We’ll redirect you to DIY or
              guided if that fits better.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <Link className="btn btn-outline btn-sm" to="/contact">
                Request a proposal
              </Link>
              <Link className="btn btn-primary btn-sm" to={standardEaMiningEnquiryPath()}>
                Standard EA mining
              </Link>
            </div>
          </article>
        </div>

        <div className="card" style={{ marginBottom: "2rem" }}>
          <h3>Quick chooser</h3>
          <ul className="check-list">
            <li>
              <strong>Need it today</strong> → DIY library
            </li>
            <li>
              <strong>Need a professional PDF this week</strong> → guided report from{" "}
              {formatAud(GUIDED_REPORT_FROM_CENTS)} ex GST
            </li>
            <li>
              <strong>Need ongoing specialist judgment</strong> → consultancy from{" "}
              {formatAud(CONSULTANCY_FROM_CENTS)} ex GST ({formatAud(tier3.totalCents)} incl. GST)
            </li>
            <li>
              <strong>Need a Standard EA for mining</strong> → {STANDARD_EA_MINING.priceLine} (
              {standardEa.incl} incl. GST)
            </li>
          </ul>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginTop: "1rem" }}>
            <Link className="btn btn-primary" to="/resources">
              Start free
            </Link>
            <Link className="btn btn-outline" to="/reports">
              Guided reports
            </Link>
          </div>
        </div>

        <h3>Standard Environmental Authority applications for mining</h3>
        <p className="muted">
          Named consultancy to prepare and lodge a Standard EA for a mining project in Australia.
          Queensland-style Standard EA is the usual frame. Enquiry first. Not a DIY download.
        </p>
        <div className="card" style={{ marginTop: "1rem", marginBottom: "2rem" }}>
          <p className="price" style={{ marginTop: 0 }}>
            {standardEa.plusGstLine}
          </p>
          <ul className="check-list">
            <li>Fee {standardEa.fee}</li>
            <li>GST {standardEa.gst}</li>
            <li>
              <strong>Total {standardEa.incl} incl. GST</strong>
            </li>
          </ul>
          <p className="muted" style={{ marginTop: "0.85rem" }}>
            {STANDARD_EA_MINING.description}
          </p>
          <Link className="btn btn-primary" to={standardEaMiningEnquiryPath()} style={{ marginTop: "1rem" }}>
            Enquire for Standard EA mining
          </Link>
        </div>

        <h3>Urban development package — per lot</h3>
        <p className="muted">
          For subdivision and urban projects that need a scoped environmental package — not a
          default for every lot if a guided report will do.
        </p>
        <div className="table-wrap" style={{ marginTop: "1rem", marginBottom: "2rem" }}>
          <table className="data">
            <thead>
              <tr>
                <th>Component</th>
                <th>Amount (ex GST)</th>
                <th>GST</th>
                <th>Incl. GST</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Urban development environmental package (per lot)</td>
                <td>{formatAud(urban.subtotalCents)}</td>
                <td>{formatAud(urban.gstCents)}</td>
                <td>
                  <strong>{formatAud(urban.totalCents)}</strong>
                </td>
              </tr>
              <tr>
                <td colSpan={4} className="muted">
                  Final scope confirmed per project. Multi-lot discounts by proposal. Consultancy
                  engagements start from {formatAud(CONSULTANCY_FROM_CENTS)} ex GST.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <h3>How payment works</h3>
          <ul className="muted">
            <li>Domestic AU cards via Stripe: 1.7% + A$0.30 per successful charge</li>
            <li>International cards: 3.5% + A$0.30</li>
            <li>Tax invoices in your dashboard after purchase</li>
            <li>Consultancy may use proposal + invoice or deposit rather than self-serve checkout</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
